import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import moment from "moment";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import {
  CustomTreeData,
  DataTypeProvider,
  IntegratedSelection,
  SelectionState,
  TreeDataState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableFixedColumns,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { usePerformanceTrackingChartMetric } from "../../hooks/use-chart-state";
import { useShowTableSelectColumn } from "../../hooks/use-show-table-select-column";
import {
  MaxIndentLevelProvider,
  useAllSelectedRowIds,
  useExpandedRowIds,
} from "../contexts";
import { useSelectedRows } from "../utils/row-selection";

import { TableCell, TableRow } from "./body-components";
import {
  ACTUAL_FORECAST_PERIOD_ID,
  ACTUAL_WEEKLY_SPEND_ID,
  AVG_WEEKLY_SPEND_ID,
  BUDGETED_WEEKLY_SPEND_ID,
  BUDGET_FORECAST_PERIOD_ID,
  COLUMNS,
  DEFAULT_COLUMN_WIDTHS,
  FIXED_NAME_COLUMN_ID,
  MTA_CAC_ID,
  MTA_REVENUE_ID,
  MTA_ROAS_ID,
  MTA_TRANSACTIONS_ID,
  PACING_STATUS_COLUMN_ID,
  ROW_HEIGHT,
  SELECTION_COLUMN_WIDTH,
  SPEND_THERMOMETER_ID,
  TOTAL_BUDGET_COLUMN_ID,
} from "./constants";
import {
  AvgWeeklySpendDataFormatter,
  DimensionDataFormatter,
  ForecastPeriodDataFormatter,
  MetricDataFormatter,
  SpendThermometerDataFormatter,
  WeeklySpendDataFormatter,
  PacingStatusDataFormatter,
  TotalBudgetDataFormatter,
} from "./data-type-formatters";
import { FixedRow } from "./fixed-row-components";
import { TableHeaderCell, TableHeaderContent } from "./header-components";
import { TableSelectionHeaderCell } from "./header-components/table-selection-header-cell";
import {
  TableTreeColumnCell,
  TableTreeColumnExpandButton,
  TableTreeColumnIndent,
} from "./table-tree-components";
import { TableSelectionCell } from "./table-tree-components/table-selection-cell";

import "../mmm-performance-tracking-table.scss";

export enum WeekStatus {
  COMPLETE = "Complete",
  CURRENT = "Current",
  UPCOMING = "Upcoming",
}

export type WeekData = {
  actual: number | null;
  budgeted: number | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  status: WeekStatus;
};

export type TableRowTypeData = {
  weeklyMetrics: WeekData[];
  actualSpend: number;
  actualSpendMix: number;
  budgetedSpendToDate: number;
  budgetedSpend: number;
  budgetedSpendMix: number;
  mtaRevenue: number;
  mtaTransactions: number;
  mtaRoas: number;
  mtaCac: number;
};
export type TableRowTypeMetaData = {
  breakdowns: Record<string, string>;
  currentBreakdown: { key: string; value: string };
};
export type TableRowType = {
  id: string;
  key: string;
  isHeaderRow?: boolean;
  index: number;
  data: TableRowTypeData;
  children: TableRowType[];
  metadata: TableRowTypeMetaData;
  hasChildren: boolean;
  rowType: "normal";
};

export type LoadingRowType = {
  key: string;
  rowType: "loading";
};

export type PerformanceTrackingTableRow = TableRowType | LoadingRowType;

interface Props {
  rows: PerformanceTrackingTableRow[];
  loading: boolean;
  maxIndentLevel: number;
  headingRow: PerformanceTrackingTableRow | undefined;
}

const getChildRows = (row: TableRowType, rootRows: any[] | null) => {
  if (row === null) {
    return rootRows;
  }

  if (row.children?.length) {
    return row.children;
  }

  return null;
};

export const Table = ({ rows, loading, maxIndentLevel, headingRow }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const [expandedRowIds, setExpandedRowIds] = useExpandedRowIds();

  const selectedRowIds = useSelectedRows(rows);

  const [, setAllSelectedRowIds] = useAllSelectedRowIds();
  useEffect(() => {
    setAllSelectedRowIds(selectedRowIds);
  }, [selectedRowIds, setAllSelectedRowIds]);

  const { selectedCycle } = useSelectedCycle();
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  useEffect(() => {
    const start = moment(selectedCycle.date_range.start_date);
    const end = moment(selectedCycle.date_range.end_date);
    const weeksDiff = end.diff(start, "weeks") + 1;
    setColumnWidths((prevColumnWidths) => {
      return prevColumnWidths.map((columnWidth) => {
        if (
          [ACTUAL_WEEKLY_SPEND_ID, BUDGETED_WEEKLY_SPEND_ID].includes(
            columnWidth.columnName,
          )
        ) {
          return {
            columnName: columnWidth.columnName,
            width: weeksDiff * 104 + 112, // 112 is the width of the first, highlighted column
          };
        }
        return columnWidth;
      });
    });
  }, [selectedCycle]);

  const [chartMetric] = usePerformanceTrackingChartMetric();
  const [showSelectColumn, setShowSelectColumn] = useShowTableSelectColumn();

  useEffect(() => {
    if (chartMetric === "kpi") {
      setShowSelectColumn(false);
    } else {
      setShowSelectColumn(true);
    }
  }, [chartMetric, setShowSelectColumn]);

  // this gets the aggregate values
  const FixedTopRow = useMemo(
    () =>
      FixedRow(headingRow!, expandedRowIds, () => {
        trackMMMEvent({
          component:
            TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
          action: "onChange",
          actionInfo: "Collapse all rows",
        });

        setExpandedRowIds([]);
      }),
    [expandedRowIds, headingRow, setExpandedRowIds, trackMMMEvent],
  );

  const possibleLoadingRows = useMemo(() => {
    if (loading) {
      return Array.from({ length: 15 }).map((_, index) => ({
        key: `loading-${index}`,
        rowType: "loading",
        index,
      }));
    } else {
      return rows;
    }
  }, [loading, rows]);

  return (
    <MaxIndentLevelProvider maxIndentLevel={maxIndentLevel}>
      <div className="mmm-performance-tracking-table h-[50rem] py-1 rounded-md relative">
        <Grid
          columns={COLUMNS}
          rows={possibleLoadingRows}
          getRowId={(row) => row.key}
        >
          <TreeDataState
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={(values) => {
              trackMMMEvent({
                component:
                  TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
                action: "onChange",
                actionInfo: "Change expanded rows",
              });

              setExpandedRowIds(values as string[]);
            }}
          />
          <CustomTreeData getChildRows={getChildRows} />
          <VirtualTable
            height="auto"
            estimatedRowHeight={ROW_HEIGHT}
            headComponent={FixedTopRow as React.ComponentType<object>}
            rowComponent={(props) => {
              const row = props.row;
              if (row.rowType === "loading") {
                return (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={clsx(
                      props.row.index % 2 === 0 ? "even" : "odd",
                      "relative",
                    )}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <td className="sticky left-0 bg-v2-primary z-1 border-b border-v2-secondary"></td>
                    <td
                      className="sticky bg-v2-primary z-1 border-b border-v2-secondary"
                      style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
                    >
                      <div className="flex items-center h-full ml-2">
                        <img
                          src={salesPlatformColumnLoading}
                          alt="Loading table"
                          style={{
                            height: "2rem",
                          }}
                        />
                      </div>
                    </td>
                    {Array.from({ length: COLUMNS.length }).map((_, i) => (
                      <td key={i} className="border-b border-v2-secondary">
                        <div className="animate-pulse flex-grow h-full flex items-center">
                          <div className="ml-2 h-2 w-2/5 bg-gray-2"></div>
                        </div>
                      </td>
                    ))}
                  </motion.tr>
                );
              } else {
                return <TableRow {...props}></TableRow>;
              }
            }}
            cellComponent={TableCell}
          />
          <TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={setColumnWidths}
            defaultColumnWidths={DEFAULT_COLUMN_WIDTHS}
          />
          <SelectionState
            selection={selectedRowIds}
            // DO NOTHING, we will not use this cos it sucks
            onSelectionChange={(_rowIds: (string | number)[]) => undefined}
          />
          <IntegratedSelection />
          <TableSelection
            showSelectAll={showSelectColumn}
            showSelectionColumn={showSelectColumn}
            selectionColumnWidth={SELECTION_COLUMN_WIDTH}
            headerCellComponent={TableSelectionHeaderCell}
            cellComponent={TableSelectionCell}
          />
          <TableHeaderRow
            cellComponent={TableHeaderCell}
            contentComponent={TableHeaderContent}
          />
          {/* Custom column cells */}
          <DataTypeProvider
            for={[FIXED_NAME_COLUMN_ID]}
            formatterComponent={(props) => (
              <DimensionDataFormatter {...props} />
            )}
          />
          <DataTypeProvider
            for={[PACING_STATUS_COLUMN_ID]}
            formatterComponent={PacingStatusDataFormatter}
          />
          <DataTypeProvider
            for={[SPEND_THERMOMETER_ID]}
            formatterComponent={SpendThermometerDataFormatter}
          />
          <DataTypeProvider
            for={[TOTAL_BUDGET_COLUMN_ID]}
            formatterComponent={TotalBudgetDataFormatter}
          />
          <DataTypeProvider
            for={[BUDGET_FORECAST_PERIOD_ID, ACTUAL_FORECAST_PERIOD_ID]}
            formatterComponent={ForecastPeriodDataFormatter}
          />
          <DataTypeProvider
            for={[BUDGETED_WEEKLY_SPEND_ID, ACTUAL_WEEKLY_SPEND_ID]}
            formatterComponent={WeeklySpendDataFormatter}
          />
          <DataTypeProvider
            for={[AVG_WEEKLY_SPEND_ID]}
            formatterComponent={AvgWeeklySpendDataFormatter}
          />
          <DataTypeProvider
            for={[MTA_REVENUE_ID, MTA_TRANSACTIONS_ID, MTA_ROAS_ID, MTA_CAC_ID]}
            formatterComponent={MetricDataFormatter}
          />
          <TableTreeColumn
            for={FIXED_NAME_COLUMN_ID}
            cellComponent={(props) => <TableTreeColumnCell {...props} />}
            expandButtonComponent={(props) => (
              <TableTreeColumnExpandButton {...props} />
            )}
            indentComponent={TableTreeColumnIndent}
          />
          <TableFixedColumns
            leftColumns={[TableSelection.COLUMN_TYPE, FIXED_NAME_COLUMN_ID]}
          />
        </Grid>
      </div>
    </MaxIndentLevelProvider>
  );
};
